import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";
import type { HeroCarousel as HeroCarouselType } from "@shared/types/carousel";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface HeroCarouselProps {
  data: HeroCarouselType;
  className?: string;
}

export function HeroCarousel({ data, className = "" }: HeroCarouselProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Detectar si es móvil
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // 🎠 LOG: Data recibida en el componente (ya transformada)
  console.log("🎠 [HERO CAROUSEL COMPONENT] Data recibida:", data);

  if (!data.slides || data.slides.length === 0) {
    return (
      <div className="bg-gray-200 h-96 flex items-center justify-center">
        <p className="text-gray-600">No hay slides disponibles</p>
      </div>
    );
  }

  return (
    <div className={`h-[600px] w-full ${className}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={1}
        spaceBetween={0}
        navigation={true}
        pagination={{ clickable: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        onSwiper={() => {
          // Swiper initialized successfully
        }}
        onInit={() => {
          // Swiper ready
        }}
        style={{ height: "100%", width: "100%" }}
      >
        {data.slides.map((slide, index) => (
            <SwiperSlide key={slide.id || index}>
              <div
                style={{
                  height: "100%",
                  width: "100%",
                  backgroundImage: `url(${isMobile ? slide.imageMobileUrl || slide.imageUrl : slide.imageUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "bold",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.7)",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: isMobile ? "24px" : "32px", marginBottom: "10px" }}>
                    {slide.title}
                  </div>
                  {slide.description && (
                    <div style={{ fontSize: isMobile ? "14px" : "18px", marginBottom: slide.ctaText ? "20px" : "0" }}>
                      {slide.description}
                    </div>
                  )}
                  {slide.ctaText && (
                    <div style={{ marginTop: "20px" }}>
                      {slide.ctaLink ? (
                        <Link
                          to={slide.ctaLink}
                          className="inline-block px-6 py-3 bg-white text-[#282828] font-semibold rounded-md hover:bg-gray-100 transition-colors shadow-lg"
                          style={{
                            fontSize: isMobile ? "14px" : "16px",
                          }}
                        >
                          {slide.ctaText}
                        </Link>
                      ) : (
                        <button
                          className="inline-block px-6 py-3 bg-white text-[#282828] font-semibold rounded-md hover:bg-gray-100 transition-colors shadow-lg"
                          style={{
                            fontSize: isMobile ? "14px" : "16px",
                          }}
                        >
                          {slide.ctaText}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
